import { XMarkIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';
import React, { useEffect, useCallback, useRef, useState, useId } from 'react';
import { createPortal } from 'react-dom';

import { useDialogFocus } from './useDialogFocus';
import { IconButton } from '../Button';

import type {
  BottomSheetProps,
  BottomSheetHeaderProps,
  BottomSheetBodyProps,
  BottomSheetFooterProps,
  BottomSheetHeight,
} from './Modal.types';

import { useBodyScrollLock } from '@/hooks';
import { useBreakpoint } from '@/hooks';

/**
 * Tablet breakpoint threshold for action sheet centering (per PRD)
 */
const TABLET_BREAKPOINT = 600;

/**
 * Height classes for bottom sheet
 */
const heightClasses: Record<BottomSheetHeight, string> = {
  auto: 'h-auto max-h-[90vh]',
  partial: '', // Handled dynamically via style
  full: 'h-[100dvh]',
};

/**
 * BottomSheetHeader component
 * Header with optional drag handle, title, back button, and close button.
 *
 * The title (or a custom header) sits left, after the back button when there
 * is one, and takes the row up to the ✕, as on the centred modal and mobile's
 * sheet. Centring it over empty side slots wrapped ordinary titles on a phone.
 */
export function BottomSheetHeader({
  title,
  showCloseButton = true,
  onClose,
  showDragHandle = true,
  onBack,
  children,
  className = '',
  ...props
}: BottomSheetHeaderProps) {
  // On a phone these take IconButton's 44px touch floor; the negative margin
  // lets the box overhang the title's line rather than push the header down.
  const backButton = onBack ? (
    <IconButton
      variant="ghost-muted"
      size="sm"
      icon={<ArrowLeftIcon />}
      onClick={onBack}
      className="-m-1.5"
      aria-label="Go back"
    />
  ) : null;

  const closeButton =
    showCloseButton && onClose ? (
      <IconButton
        variant="ghost-muted"
        size="sm"
        icon={<XMarkIcon />}
        onClick={onClose}
        className="-m-1.5"
        aria-label="Close"
        data-dialog-close
      />
    ) : null;

  return (
    <div className={`flex flex-col ${className}`.trim()} {...props}>
      {/* Drag Handle */}
      {showDragHandle && (
        <div className="flex justify-center py-3" data-testid="drag-handle">
          <div
            className="w-10 h-1 rounded-full bg-contrast-600"
            aria-hidden="true"
          />
        </div>
      )}

      {/* Header Content */}
      <div className="flex items-center gap-3 px-4 pb-3">
        {backButton}
        <div className="min-w-0 flex-1">
          {children ?? (
            <h2 className="font-display text-heading-md font-semibold text-neutral-50">
              {title}
            </h2>
          )}
        </div>
        {closeButton}
      </div>

      {/* Divider */}
      <div className="border-b border-stroke-subtle" />
    </div>
  );
}

/**
 * BottomSheetBody component
 * Scrollable content area
 */
export function BottomSheetBody({
  children,
  className = '',
  ...props
}: BottomSheetBodyProps) {
  return (
    <div
      // The drag guard looks for this to decide whether a downward swipe is a
      // dismissal or a scroll. See `handleDragStart`.
      data-bottom-sheet-content
      className={`
        px-4 py-4 overflow-y-auto
        flex-1
        ${className}
      `.trim()}
      {...props}
    >
      {children}
    </div>
  );
}

/** A footer's row of actions, as `ModalFooter` lines them up. */
const FOOTER_ALIGN_CLASSES: Record<
  NonNullable<BottomSheetFooterProps['align']>,
  string
> = {
  left: 'flex items-center gap-3 justify-start',
  center: 'flex items-center gap-3 justify-center',
  right: 'flex items-center gap-3 justify-end',
  between: 'flex items-center gap-3 justify-between',
};

/**
 * BottomSheetFooter component
 * Footer with action buttons and safe area padding. Given an `align`, it lays
 * its actions out in a row as `ModalFooter` does; without one it's a block,
 * laid out by what's passed in.
 */
export function BottomSheetFooter({
  children,
  align,
  className = '',
  ...props
}: BottomSheetFooterProps) {
  return (
    <div
      className={`
        px-4 pt-3
        border-t border-stroke-subtle
        pb-[calc(1rem+env(safe-area-inset-bottom))]
        ${align ? FOOTER_ALIGN_CLASSES[align] : ''}
        ${className}
      `.trim()}
      {...props}
    >
      {children}
    </div>
  );
}

/**
 * BottomSheet component
 * A slide-up sheet for mobile interactions
 *
 * Features:
 * - Slide-up from bottom animation
 * - Drag handle with swipe-to-close gesture
 * - Partial and full height modes
 * - Safe area insets for iOS
 * - Backdrop with click-to-close
 * - Escape key to close
 * - Focus kept inside while open, and handed back on close (`useDialogFocus`)
 *
 * Below `md`, every `Modal` renders as one of these, so a dialog seldom needs
 * to use it directly — reach for it only for a sheet at every width.
 *
 * @example
 * ```tsx
 * <BottomSheet
 *   isOpen={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   title="Select Option"
 *   height="partial"
 *   partialHeight={50}
 *   footer={
 *     <Button variant="primary" fullWidth>Confirm Selection</Button>
 *   }
 * >
 *   <p>Sheet content here</p>
 * </BottomSheet>
 * ```
 */
export function BottomSheet({
  isOpen,
  onClose,
  height = 'auto',
  partialHeight = 50,
  title,
  showDragHandle = true,
  swipeToClose = true,
  swipeThreshold = 30,
  showCloseButton = true,
  closeOnBackdropClick = true,
  closeOnEscape = true,
  onBack,
  className = '',
  children,
  footer,
  footerAlign,
  header,
  'aria-labelledby': ariaLabelledBy,
  'aria-label': ariaLabel,
  initialFocusRef,
  scrim = true,
  id,
  preventScroll = true,
  'data-testid': testId,
}: BottomSheetProps) {
  const [isAnimating, setIsAnimating] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const sheetRef = useRef<HTMLDivElement>(null);
  const dragStartY = useRef<number>(0);
  const sheetHeight = useRef<number>(0);
  const generatedId = useId();
  const sheetId = id ?? `bottomsheet-${generatedId}`;
  const { width } = useBreakpoint();

  // Tablet mode: viewport width >= 600px (PRD spec)
  const isTablet = width >= TABLET_BREAKPOINT;

  // Handle open/close state with animation
  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      requestAnimationFrame(() => {
        setIsAnimating(true);
      });
    } else if (shouldRender) {
      setIsAnimating(false);
      const timer = setTimeout(() => {
        setShouldRender(false);
        setDragOffset(0);
      }, 300);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [isOpen, shouldRender]);

  // Handle escape key
  useEffect(() => {
    if (!isOpen || !closeOnEscape) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, closeOnEscape, onClose]);

  // Focus on open (the ✕, not a field), Tab kept inside, handed back on close.
  const { onKeyDown: handleFocusKeyDown } = useDialogFocus({
    isOpen,
    isRendered: shouldRender,
    containerRef: sheetRef,
    initialFocusRef,
    focusFirstField: false,
  });

  // Hold the page's scroll while open — counted with every other overlay's.
  useBodyScrollLock(isOpen && preventScroll);

  // Handle backdrop click
  const handleBackdropClick = useCallback(
    (e: React.MouseEvent) => {
      if (closeOnBackdropClick && e.target === e.currentTarget) {
        onClose();
      }
    },
    [closeOnBackdropClick, onClose]
  );

  // Swipe gesture handlers
  const handleDragStart = useCallback(
    (e: React.TouchEvent | React.MouseEvent) => {
      if (!swipeToClose) return;

      // A drag that begins inside a scrolled body is a scroll, not a dismissal.
      // Without this, flicking a long list back towards its top closes the sheet
      // instead — the same guard `LayerSheet` applies, for the same reason. A
      // body already at the top still dismisses, so short sheets are unaffected.
      const content = sheetRef.current?.querySelector(
        '[data-bottom-sheet-content]'
      );
      if (
        content instanceof HTMLElement &&
        content.scrollTop > 0 &&
        e.target instanceof Node &&
        content.contains(e.target)
      ) {
        return;
      }

      setIsDragging(true);
      const clientY = 'touches' in e ? e.touches[0]?.clientY : e.clientY;
      if (clientY === undefined) return;
      dragStartY.current = clientY;
      sheetHeight.current = sheetRef.current?.offsetHeight ?? 0;
    },
    [swipeToClose]
  );

  const handleDragMove = useCallback(
    (e: React.TouchEvent | React.MouseEvent) => {
      if (!isDragging) return;

      const clientY = 'touches' in e ? e.touches[0]?.clientY : e.clientY;
      if (clientY === undefined) return;
      const diff = clientY - dragStartY.current;

      // Only allow dragging downward
      if (diff > 0) {
        setDragOffset(diff);
      }
    },
    [isDragging]
  );

  const handleDragEnd = useCallback(() => {
    if (!isDragging) return;

    setIsDragging(false);

    // Check if drag passed threshold
    const threshold = (sheetHeight.current * swipeThreshold) / 100;
    if (dragOffset > threshold) {
      onClose();
    } else {
      setDragOffset(0);
    }
  }, [isDragging, dragOffset, swipeThreshold, onClose]);

  // Add global listeners for drag
  useEffect(() => {
    if (!isDragging) return;

    const handleGlobalMove = (e: TouchEvent | MouseEvent) => {
      const clientY = 'touches' in e ? e.touches[0]?.clientY : e.clientY;
      if (clientY === undefined) return;
      const diff = clientY - dragStartY.current;
      if (diff > 0) {
        setDragOffset(diff);
      }
    };

    const handleGlobalEnd = () => {
      const threshold = (sheetHeight.current * swipeThreshold) / 100;
      if (dragOffset > threshold) {
        onClose();
      } else {
        setDragOffset(0);
      }
      setIsDragging(false);
    };

    document.addEventListener('touchmove', handleGlobalMove, {
      passive: false,
    });
    document.addEventListener('mousemove', handleGlobalMove);
    document.addEventListener('touchend', handleGlobalEnd);
    document.addEventListener('mouseup', handleGlobalEnd);

    return () => {
      document.removeEventListener('touchmove', handleGlobalMove);
      document.removeEventListener('mousemove', handleGlobalMove);
      document.removeEventListener('touchend', handleGlobalEnd);
      document.removeEventListener('mouseup', handleGlobalEnd);
    };
  }, [isDragging, dragOffset, swipeThreshold, onClose]);

  if (!shouldRender) return null;

  // Calculate height style for partial mode
  // Tablet: 50-60% viewport height (vs 50-70% on mobile per PRD)
  const heightStyle =
    height === 'partial' ? { height: `${partialHeight}vh` } : undefined;

  // Height class based on device type
  // Mobile: max 90vh, Tablet: max 60vh for auto mode
  const getHeightClass = () => {
    // On a tablet the sheet floats a margin above the bottom edge, so full
    // height leaves room for it top and bottom rather than running off the top.
    if (height === 'full') {
      return isTablet ? 'h-[calc(100dvh-2rem)]' : heightClasses.full;
    }
    if (height === 'partial') return heightClasses.partial;
    // Auto mode: tablet has lower max height
    return isTablet ? 'h-auto max-h-[60vh]' : heightClasses.auto;
  };

  const sheet = (
    <div
      className={`
        fixed inset-0 z-50
        flex items-end justify-center
        ${scrim && isAnimating ? 'bg-scrim-soft' : 'bg-transparent'}
        transition-colors duration-slow
      `.trim()}
      onClick={handleBackdropClick}
      aria-hidden={!isOpen}
      data-testid="bottom-sheet-backdrop"
    >
      <div
        ref={sheetRef}
        role="dialog"
        aria-modal="true"
        // Focusable only programmatically: the last resort for initial focus.
        tabIndex={-1}
        aria-labelledby={
          ariaLabelledBy ?? (title ? `${sheetId}-title` : undefined)
        }
        aria-label={ariaLabel}
        id={sheetId}
        data-testid={testId ?? 'bottom-sheet'}
        style={{
          ...heightStyle,
          transform: `translateY(${
            isAnimating ? dragOffset : (sheetRef.current?.offsetHeight ?? 500)
          }px)`,
        }}
        className={`
          relative w-full
          bg-contrast-950
          shadow-lift-sheet
          flex flex-col
          will-change-transform
          ${isTablet ? 'max-w-[560px] rounded-2xl mb-4' : 'rounded-t-2xl'}
          focus:outline-none
          ${getHeightClass()}
          ${isDragging ? '' : 'transition-transform duration-slow ease-out'}
          ${className}
        `.trim()}
        onTouchStart={handleDragStart}
        onTouchMove={handleDragMove}
        onTouchEnd={handleDragEnd}
        onMouseDown={handleDragStart}
        onMouseMove={handleDragMove}
        onMouseUp={handleDragEnd}
        onKeyDown={handleFocusKeyDown}
      >
        {/* Header */}
        <BottomSheetHeader
          title={title || ''}
          showCloseButton={showCloseButton}
          onClose={onClose}
          showDragHandle={showDragHandle}
          onBack={onBack}
        >
          {header ??
            (title && (
              <h2
                id={`${sheetId}-title`}
                className="font-display text-heading-md font-semibold text-neutral-50"
              >
                {title}
              </h2>
            ))}
        </BottomSheetHeader>

        {/* Body */}
        <BottomSheetBody>{children}</BottomSheetBody>

        {/* Footer */}
        {footer && (
          <BottomSheetFooter align={footerAlign}>{footer}</BottomSheetFooter>
        )}
      </div>
    </div>
  );

  return createPortal(sheet, document.body);
}

export default BottomSheet;
